import { formatDistance } from "date-fns";
import { es } from "date-fns/locale";
import { capitalize } from "lodash";
import moment from "moment";
import { FC, useCallback, useState } from "react";
import { HiOutlineEye } from "react-icons/hi";
import { MdOutlineFileCopy, MdOutlineModeEditOutline } from "react-icons/md";
import { Link } from "react-router-dom";
import { ActionIcon, Switch, Tooltip } from "rizzui";
import { toast } from "sonner";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import CustomTable from "../../../components/table/CustomTable";
import useFetch from "../../../hooks/useFetch";
import Page from "../../../layout/Page/Page";
import { menuRoutes } from "../../../router/menu";
import { RouteService } from "../../../services/route/routeService";
import { Route, RoutesApiResponse } from "../../../type/entities/route-type";
import RouteForm from "../components/RouteForm";
import RoutesFilters from "./RoutesFilters";
import { BsBuilding } from "react-icons/bs";
import DeletePopover from "../../../components/buttons/DeletePopover";
import CloneRouteModal from "../components/CloneRouteModal";

interface RoutesListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    routeSelected: Route | null;
    setRouteSelected: React.Dispatch<React.SetStateAction<Route | null>>;
}

const columnOrder = [
    'id',
    'company',
    'name',
    'description',
    'status',
    'radioInclusion',
    'routeHeadquarters',
    'createdAt',
    'updatedAt'
];

const RoutesList: FC<RoutesListProps> = ({ openFormDrawer, setOpenFormDrawer, routeSelected, setRouteSelected }) => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [cloneModalOpen, setCloneModalOpen] = useState<boolean>(false);
    const [routeToClone, setRouteToClone] = useState<Route | null>(null);
    const [cloneRouteName, setCloneRouteName] = useState<string>('');
    const service = new RouteService();

    // HOOKS

    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE RUTAS
     * @EN GETS THE LIST OF ROUTES
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.list(filters);
        return response.getResponseData() as RoutesApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLE STATUS DE LA RUTA
     * @EN TOGGLE ROUTE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(response.message);
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES CLONAR RUTA
     * @EN CLONE ROUTE
     */
    //-------------------------------------------------------------------------------------------------------------
    const cloneRoute = async (id: string, name?: string) => {
        try {
            const response = (await service.clone(id, name)).getResponseData();
            if (response.success) {
                refetch();
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ABRIR MODAL DE CLONACIÓN
     * @EN OPEN CLONE MODAL
     */
    //-------------------------------------------------------------------------------------------------------------
    const openCloneModal = (route: Route) => {
        setRouteToClone(route);
        setCloneRouteName(`${route.name} (Copia)`);
        setCloneModalOpen(true);
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES CONFIRMAR CLONACIÓN
     * @EN CONFIRM CLONE
     */
    //-------------------------------------------------------------------------------------------------------------
    const confirmClone = async () => {
        if (routeToClone && cloneRouteName.trim()) {
            setCloneModalOpen(false);
            await cloneRoute(routeToClone.id, cloneRouteName.trim());
            setRouteToClone(null);
            setCloneRouteName('');
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR MODAL DE CLONACIÓN
     * @EN CLOSE CLONE MODAL
     */
    //-------------------------------------------------------------------------------------------------------------
    const closeCloneModal = () => {
        setCloneModalOpen(false);
        setRouteToClone(null);
        setCloneRouteName('');
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES ELIMINAR RUTA
     * @EN DELETE ROUTE
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = (await service.delete(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------



    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------

    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'routes-table'}
                    columnOrder={columnOrder}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    data={orderedData()}
                    isLoading={loading}
                    floatingHorizontalScrollbar
                    isSortable
                    columnsNoSortables={['active']}
                    columnsNotShown={['company', 'routeHeadquarters']}
                    additionalColumns={[
                        {
                            id: 'headquartersCount',
                            header: 'Número de sedes',
                            render: (row: any) => {
                                const headquartersCount = row.row.original?.routeHeadquarters?.length || 0;
                                return (
                                    <div className="flex items-center justify-center">
                                        <BsBuilding className="mr-2 h-4 w-4" />
                                        {headquartersCount}
                                    </div>
                                )
                            }
                        },
                    ]}
                    overrideColumns={[
                        {
                            key: 'name',
                            columnNameKey: 'Nombre de la ruta',
                            render: (row: any) => {
                                let description = row.row.original?.description || 'Sin descripción';
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={<div className="max-w-[350px] text-ellipsis text-wrap">{description}</div>}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div className="flex flex-col">
                                            <div className="font-bold">{row.row.original.name}</div>
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            key: 'status',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.status ? 'Desactivar' : 'Activar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.status}
                                            disabled={changingStatus.includes(row.row.original.id)}
                                            onChange={() => toggleStatus(row.row.original.id, row.row.original.status)}
                                            switchKnobClassName='bg-secondary'
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => {
                                const date = row.row.original?.createdAt?.date;
                                return date ? moment(date).format('DD/MM/YYYY') : 'N/A';
                            },
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => {
                                const date = row.row.original?.updatedAt?.date;
                                return date ? (
                                    <div className="text-sm text-gray-500">
                                        <Tooltip content={moment(date).format('DD/MM/YYYY HH:mm:ss')}>
                                            <span>{capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es }))}</span>
                                        </Tooltip>
                                    </div>
                                ) : 'Nunca';
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'route', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.routes.path}/${row.id}/profile/${menuRoutes.routes.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                            >
                                                <HiOutlineEye className="h-4 w-4" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'route', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setRouteSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Clone',
                            permissions: { group: 'route', permission: 'clone' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Clonar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                openCloneModal(row);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineFileCopy className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'maintenance', permission: 'delete' },
                            hide: (row: any) => row.routeHeadquarters.length !== 0,
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={`Eliminar`}
                                                description={`¿Estás seguro de que deseas eliminar esta ruta?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as RoutesApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Rutas'} onClear={() => resetFilters(50)}>
                <RoutesFilters filters={filters} updateFilters={updateFilters} />
            </FilterDrawerView>

            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    routeSelected ?
                        "Completa el formulario para editar los datos de la ruta" :
                        "Completa el formulario para crear una nueva ruta"
                }
                drawerTitle={routeSelected ? 'Editar Ruta' : 'Crear Ruta'}
                submitText={routeSelected ? 'Guardar Ruta' : 'Crear Ruta'}>
                <RouteForm
                    setOpenModal={setOpenFormDrawer}
                    refetch={refetch}
                    route={routeSelected}
                />
            </FormDrawerView>

            {/* Modal de clonación */}
            <CloneRouteModal
                isOpen={cloneModalOpen}
                onClose={closeCloneModal}
                onConfirm={confirmClone}
                routeName={cloneRouteName}
                onRouteNameChange={setCloneRouteName}
            />
        </Page>
    );
};

export default RoutesList;